/* Sagestry kit: the page engine of the sages- sections. No library.
   Ported from design/mockups/draft/kit/draft.js, one part at a time as pages are built.

   1  sky         the page is one day: a fixed colour behind open sections, blended between them as you scroll
   2  entrances   text fades in; props arrive in stop-motion poses at 12 frames a second
   3  scroll      drift, and loop videos (only the one most in view plays)

   Markup contract (see snippets/sages-words.liquid and the sages- sections):
     [data-sg-sky="#hex"]     a section's sky colour
     [data-sg-sky-next="#hex"]  a marker that gives the next section a sky colour
     [data-sg-block]          something that is "seen" once (gets .sg-seen)
     [data-sg-in="text|hop|settle|pop|grow|drop|fade|walk|walk-l"]   how a thing arrives; style="--sg-i:n" staggers it
     [data-sg-drift="16"]     a picture that drifts a few pixels against the scroll
     video[data-sg-loop][data-src]   a loop that loads when near and plays when most in view */

const d = document;
const root = d.documentElement;
const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
const editing = root.classList.contains('shopify-design-mode');
const FPS = 12;

/** @returns {boolean} true when nothing should move */
const still = () => calm || editing;
const clamp = (/** @type {number} */ x, /** @type {number} */ a, /** @type {number} */ b) => Math.max(a, Math.min(b, x));
const $$ = (/** @type {string} */ s, /** @type {ParentNode} */ c = d) => Array.from(c.querySelectorAll(s));

/* ---------------------------------------------------------------- 1 sky */
/** @param {string} c */
function hex(c) {
  c = c.replace('#', '');
  if (c.length === 3) c = c.replace(/./g, '$&$&');
  return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
}
const lin = (/** @type {number} */ v) => ((v /= 255) <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
const gam = (/** @type {number} */ v) =>
  clamp(Math.round((v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055) * 255), 0, 255);

/** OKLab, so the blend between two colours passes through a natural middle. @param {number[]} c */
function toLab(c) {
  const r = lin(c[0]);
  const g = lin(c[1]);
  const b = lin(c[2]);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** @param {number[]} o */
function fromLab(o) {
  const l = Math.pow(o[0] + 0.3963377774 * o[1] + 0.2158037573 * o[2], 3);
  const m = Math.pow(o[0] - 0.1055613458 * o[1] - 0.0638541728 * o[2], 3);
  const s = Math.pow(o[0] - 0.0894841775 * o[1] - 1.291485548 * o[2], 3);
  return `rgb(${gam(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s)},${gam(
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
  )},${gam(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s)})`;
}
const smooth = (/** @type {number} */ t) => ((t = clamp(t, 0, 1)), t * t * (3 - 2 * t));
const mixLab = (/** @type {number[]} */ a, /** @type {number[]} */ b, /** @type {number} */ t) => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/** @type {{el: HTMLElement, lab: number[], dark: boolean, foot: boolean}[]} */
let skySecs = [];
/** @type {HTMLElement | null} */
let sky = null;
let lastSky = '';

function readSky() {
  // a marker section (sections/sages-sky.liquid) lends its colour to the section after it
  for (const mark of /** @type {HTMLElement[]} */ ($$('[data-sg-sky-next]'))) {
    const next = /** @type {HTMLElement | null} */ (mark.closest('.shopify-section')?.nextElementSibling ?? null);
    if (!next || next.matches('.sg-sky-mark')) continue;
    next.dataset.sgSky = mark.dataset.sgSkyNext || '';
    next.classList.toggle('sg-sec--dark', mark.hasAttribute('data-sg-dark'));
  }
  skySecs = /** @type {HTMLElement[]} */ ($$('[data-sg-sky]'))
    .filter((el) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(el.dataset.sgSky || ''))
    .map((el) => ({
      el,
      lab: toLab(hex(el.dataset.sgSky || '')),
      dark: el.classList.contains('sg-sec--dark'),
      foot: el.hasAttribute('data-sg-foot'),
    }));
  // a page with no tagged section keeps the theme's own background
  const wanted = skySecs.some((s) => !s.foot);
  if (wanted && !sky) {
    sky = d.createElement('div');
    sky.className = 'sg-sky';
    sky.setAttribute('aria-hidden', 'true');
    d.body.prepend(sky);
  }
  d.body.classList.toggle('sg-has-sky', wanted);
  if (sky) sky.hidden = !wanted;
  lastSky = '';
}

function paintSky() {
  if (!sky || sky.hidden || !skySecs.length) return;
  const mid = innerHeight * 0.55;
  const band = innerHeight * 0.32;
  const rs = skySecs.map((s) => s.el.getBoundingClientRect());
  let i = rs.findIndex((r) => r.top <= mid && r.bottom > mid);
  if (i < 0) {
    // between tagged sections: the nearest one
    let best = 1e9;
    rs.forEach((r, k) => {
      const dd = Math.min(Math.abs(r.top - mid), Math.abs(r.bottom - mid));
      if (dd < best) {
        best = dd;
        i = k;
      }
    });
  }
  // the night footer is solid: behind a light page's foot the sky keeps that page's colour
  if (i > 0 && skySecs[i].foot && !skySecs[i - 1].dark) i--;
  let lab = skySecs[i].lab;
  const r = rs[i];
  if (i > 0 && mid - r.top < band) {
    lab = mixLab(skySecs[i - 1].lab, lab, smooth(0.5 + (mid - r.top) / (2 * band)));
  } else if (i < rs.length - 1 && r.bottom - mid < band && !(skySecs[i + 1].foot && !skySecs[i].dark)) {
    lab = mixLab(lab, skySecs[i + 1].lab, smooth(0.5 - (r.bottom - mid) / (2 * band)));
  }
  const c = fromLab(lab);
  if (c !== lastSky) {
    sky.style.setProperty('--sg-sky-now', c);
    lastSky = c;
  }
}

/* ---------------------------------------------------------------- 2 entrances */
// Poses for props, one per frame at 12 fps. Each held until the next: stop motion, never a tween.
// x, y in px; r in degrees; s scale; sx, sy squash; o opacity. Missing values are the resting pose.
/** @typedef {{x?: number, y?: number, r?: number, s?: number, sx?: number, sy?: number, o?: number}} Pose */
/** @type {Record<string, Pose[]>} */
const POSES = {
  hop: [{ y: 38, sy: 0.9, sx: 1.06, o: 0 }, { y: 20, sy: 0.96, o: 1 }, { y: -9, sy: 1.05, sx: 0.97 }, { y: -4, sy: 1.01 }, { y: 2, sy: 0.96, sx: 1.03 }, { y: 0, sy: 1.01 }, {}],
  pop: [{ s: 0.25, o: 0 }, { s: 0.7 }, { s: 1.12 }, { s: 0.94 }, { s: 1.04 }, {}],
  grow: [{ sy: 0.04, o: 0 }, { sy: 0.45 }, { sy: 0.9 }, { sy: 1.09 }, { sy: 0.96 }, { sy: 1.02 }, {}],
  drop: [{ y: -46, o: 0 }, { y: -26 }, { y: -8 }, { y: 5, sy: 0.97 }, { y: -2 }, { y: 1 }, {}],
  settle: [{ r: -5, y: 12, o: 0 }, { r: 3, y: -4 }, { r: -1.6, y: 1 }, { r: 0.8 }, { r: -0.3 }, {}],
  fade: [{ o: 0 }, { o: 0.35 }, { o: 0.7 }, {}],
  walk: [{ x: -110, o: 0 }, { x: -92, y: -5, r: -2.5 }, { x: -76, r: 2 }, { x: -60, y: -5, r: -2.5 }, { x: -44, r: 2 }, { x: -30, y: -5, r: -2 }, { x: -17, r: 1.5 }, { x: -7, y: -3, r: -1 }, {}],
};

// the same walk, coming in from the right
POSES['walk-l'] = POSES.walk.map((p) => ({ ...p, ...(p.x != null && { x: -p.x }), ...(p.r != null && { r: -p.r }) }));

/** @param {Pose} p @param {number} jit */
function tf(p, jit) {
  const s = p.s ?? 1;
  return `translate(${p.x || 0}px,${p.y || 0}px) rotate(${((p.r || 0) + jit).toFixed(2)}deg) scale(${(p.sx ?? 1) * s},${
    (p.sy ?? 1) * s
  })`;
}

/** @param {HTMLElement} el @param {string} name @param {number} delayFrames */
function stepAnim(el, name, delayFrames) {
  const poses = POSES[name] || POSES.hop;
  const n = poses.length;
  if (!el.animate || still()) {
    el.classList.add('sg-done');
    return;
  }
  const kf = poses.map((p, k) => ({
    // a hand is never quite the same twice
    transform: tf(p, k && k < n - 1 ? (Math.random() - 0.5) * 1.6 : 0),
    opacity: p.o ?? 1,
    offset: k / (n - 1),
    easing: 'steps(1, end)',
  }));
  el.style.transformOrigin = '50% 100%';
  const a = el.animate(kf, { duration: ((n - 1) * 1000) / FPS, delay: (delayFrames * 1000) / FPS, fill: 'both' });
  a.onfinish = () => {
    el.classList.add('sg-done');
    a.cancel();
  };
}

const watched = new WeakSet();

// Text fades in when its section arrives. A prop waits until the prop itself is well into view, so its
// entrance is seen rather than finished before it reaches the screen.
const propIO = still()
  ? null
  : new IntersectionObserver(
      (es) => {
        for (const e of es) {
          const el = /** @type {HTMLElement} */ (e.target);
          const need = Math.min(e.boundingClientRect.height * 0.38, innerHeight * 0.22);
          if (e.isIntersecting && (e.intersectionRatio >= 0.38 || e.intersectionRect.height >= need)) {
            propIO?.unobserve(el);
            stepAnim(el, el.dataset.sgIn || 'hop', 1 + (+getComputedStyle(el).getPropertyValue('--sg-i') || 0));
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: [0, 0.1, 0.2, 0.3, 0.38, 0.6] }
    );

const blockIO = still()
  ? null
  : new IntersectionObserver(
      (es) => {
        for (const e of es) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('sg-seen');
          e.target.dispatchEvent(new CustomEvent('sg:seen'));
          blockIO?.unobserve(e.target);
        }
      },
      { rootMargin: '0px 0px -16% 0px', threshold: 0.06 }
    );

/** @param {ParentNode} scope */
function watchEntrances(scope) {
  for (const el of /** @type {HTMLElement[]} */ ($$('[data-sg-in]', scope))) {
    if (el.dataset.sgIn === 'text' || watched.has(el)) continue;
    watched.add(el);
    if (propIO) propIO.observe(el);
    else el.classList.add('sg-done');
  }
  for (const b of $$('[data-sg-block]', scope)) {
    if (watched.has(b)) continue;
    watched.add(b);
    if (blockIO) blockIO.observe(b);
    else b.classList.add('sg-seen');
  }
}

/* ---------------------------------------------------------------- 3 scroll */
/** @type {HTMLElement[]} */
let drifts = [];

/* The menu bar turns to night glass while it lies over a dark section or the footer (the styles are in
   sages-kit.css under "the menu bar"). What lies just under the bar's lower edge decides. */
const bar = d.getElementById('header-component');
let barDark = false;
function barOverDark() {
  if (!bar) return;
  const r = bar.getBoundingClientRect();
  if (r.bottom <= 0 || r.height === 0) return;
  const under = d.elementFromPoint(innerWidth / 2, r.bottom + 2);
  const dark = !!(under && under.closest('.sg-sec--dark, .sg-foot, [data-sg-night]'));
  if (dark !== barDark) {
    barDark = dark;
    root.classList.toggle('sg-over-dark', dark);
  }
}

function scroll() {
  paintSky();
  barOverDark();
  if (still()) return;
  const vh = innerHeight;
  for (const el of drifts) {
    const sec = el.closest('[data-sg-block]') || el.parentElement;
    if (!sec) continue;
    const r = sec.getBoundingClientRect();
    if (r.bottom < -200 || r.top > vh + 200) continue;
    const p = (vh - r.top) / (vh + r.height);
    el.style.translate = `0 ${((0.5 - p) * 2 * (+(el.dataset.sgDrift || 16) || 16)).toFixed(1)}px`;
  }
}

let ticking = false;
function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    ticking = false;
    scroll();
  });
}

// loop videos: load when near, and only the one most in view plays
/** @type {Map<HTMLVideoElement, number>} */
const ratios = new Map();
const nearIO = new IntersectionObserver(
  (es) => {
    for (const e of es) {
      const v = /** @type {HTMLVideoElement} */ (e.target);
      if (e.isIntersecting && !v.src && v.dataset.src && !still()) {
        v.src = v.dataset.src;
        v.preload = 'auto';
      }
    }
  },
  { rootMargin: '600px 0px' }
);
const visIO = new IntersectionObserver(
  (es) => {
    for (const e of es) ratios.set(/** @type {HTMLVideoElement} */ (e.target), e.intersectionRatio);
    /** @type {HTMLVideoElement | null} */
    let best = null;
    let br = 0.3;
    ratios.forEach((r, v) => {
      if (v.isConnected && r > br) {
        best = v;
        br = r;
      }
    });
    ratios.forEach((_, v) => {
      if (v === best && !still() && !d.hidden) {
        if (!v.src && v.dataset.src) v.src = v.dataset.src;
        v.play()?.catch(() => {});
        v.classList.add('sg-on');
      } else {
        v.pause();
      }
    });
  },
  { threshold: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1] }
);

/** @param {ParentNode} scope */
function watchLoops(scope) {
  for (const v of /** @type {HTMLVideoElement[]} */ ($$('video[data-sg-loop]', scope))) {
    if (watched.has(v)) continue;
    watched.add(v);
    v.muted = true;
    v.playsInline = true;
    v.loop = true;
    nearIO.observe(v);
    visIO.observe(v);
  }
}

/* ---------------------------------------------------------------- start */
/** Reads the page again. Safe to call any number of times (the theme editor adds and removes sections). */
const MOVING =
  '[data-sg-stars],[data-sg-clip],[data-sg-flyer],[data-sg-poses],[data-sg-puppet],[data-sg-rise],[data-sg-sundial],[data-sg-daylight],[data-sg-bubbles],[data-sg-door],[data-sg-story],[data-sg-wisps],[data-sg-index]';
let motion = false;

/* The picture that hops into the cart (Horizon reads it from the button's holder) is the product's own pack
   where there is one: on a tile, the pack shown on it; on a product page, the pack named by the page. */
function packs() {
  for (const card of $$('.product-card')) {
    const pack = /** @type {HTMLImageElement | null} */ (card.querySelector('.sg-stage__pack'));
    const holder = /** @type {HTMLElement | null} */ (card.querySelector('add-to-cart-component'));
    if (pack && holder) holder.dataset.productVariantMedia = pack.currentSrc || pack.src;
  }
  // @ts-ignore set by snippets/sages-kit.liquid
  const own = window.SagesKit?.pack;
  if (!own) return;
  for (const holder of /** @type {HTMLElement[]} */ ($$('add-to-cart-component'))) {
    if (!holder.closest('.product-card')) holder.dataset.productVariantMedia = own;
  }
}

function init() {
  if (!motion && d.querySelector(MOVING)) {
    motion = true;
    // @ts-ignore set by snippets/sages-kit.liquid
    const url = window.SagesKit?.motion;
    if (url) import(url).catch(() => {});
  }
  readSky();
  watchEntrances(d);
  watchLoops(d);
  drifts = /** @type {HTMLElement[]} */ ($$('[data-sg-drift]'));
  packs();
  scroll();
}

root.classList.add('sg-js');
// tells the small script in snippets/sages-kit.liquid that the engine arrived
root.dataset.sgKit = 'ready';
// Horizon scrolls the page inside .page-wrapper on wide screens and in the window on narrow ones. Scroll events
// do not bubble, so one capturing listener on the document hears both. Positions are always read from
// getBoundingClientRect, never from scrollY, for the same reason.
d.addEventListener('scroll', onScroll, { passive: true, capture: true });
addEventListener('resize', onScroll);
d.addEventListener('shopify:section:load', init);
d.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
d.addEventListener('shopify:section:reorder', init);
init();
